// The run-history days window for `/runs/:tab?days=N` (sc-114200): the selector's options, the URL rule, and
// how the window is described. Pure — no React, no router.

/** Every window the selector offers, in display order. The largest is the retained history (30 days). */
export const RUN_DAYS = [1, 2, 3, 7, 14, 30] as const;
export type RunDays = (typeof RUN_DAYS)[number];

/** The window a URL without `days` renders. */
export const DEFAULT_RUN_DAYS: RunDays = 7;

const DAYS_PARAM = "days";

/** The in-flight tabs' description: they show current state, so the window does not apply to them. */
export const IN_FLIGHT_WINDOW_NOTE = "Current state — not filtered by days.";

const TRUNCATED_NOTE = "Truncated at the server's scan limit — older runs in this window may be missing.";

/**
 * The window a location's search string selects: no `days` → `DEFAULT_RUN_DAYS`; exactly one of `RUN_DAYS`
 * spelled as a plain integer → it; anything else → `null` (the page replaces the URL without it).
 */
export function readRunDays(search: string): RunDays | null {
  const raw = new URLSearchParams(search).get(DAYS_PARAM);
  if (raw === null) return DEFAULT_RUN_DAYS;
  return RUN_DAYS.find((days) => String(days) === raw) ?? null;
}

/** `search` with `days` set to `days` (other params kept), or removed when `days` is null. "" or "?…". */
export function withRunDays(search: string, days: RunDays | null): string {
  const params = new URLSearchParams(search);
  if (days === null) params.delete(DAYS_PARAM);
  else params.set(DAYS_PARAM, String(days));
  const next = params.toString();
  return next ? `?${next}` : "";
}

/** The Succeeded/Failed grid description: the window, plus a notice when the server truncated the scan. */
export function runWindowDescription(days: RunDays, truncated: boolean): string {
  const window = `Last ${days} ${days === 1 ? "day" : "days"}.`;
  return truncated ? `${window} ${TRUNCATED_NOTE}` : window;
}
